import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { OrderHistory } from '../../services/types';
import { parseCalendarDate } from '../../utils/calendarDate';

interface PartOrderHistoryTableProps {
  orderHistory: OrderHistory[];
}

export const PartOrderHistoryTable: React.FC<PartOrderHistoryTableProps> = ({
  orderHistory,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const totalQty = orderHistory.reduce(
    (sum, record) => sum + record.orderQty,
    0
  );

  const handleOrderClick = (orderNum: number) => {
    navigate(`/sales-orders/${orderNum}`);
  };

  return (
    <div className="bg-white rounded border border-gray-300 shadow-sm flex flex-col">
      {/* Header */}
      <div className="bg-orange-50 px-4 py-3 border-b border-orange-200 flex-shrink-0">
        <h3 className="text-lg font-bold text-orange-900">
          {t('parts.orderHistory')}
        </h3>
        <p className="text-xs text-orange-700">
          {orderHistory.length}{' '}
          {orderHistory.length === 1
            ? t('parts.records')
            : t('parts.recordPlural')}
          {totalQty !== 0 && ` • Total: ${totalQty.toLocaleString()}`}
        </p>
      </div>

      {/* Content */}
      {orderHistory.length > 0 ? (
        <div className="overflow-x-auto flex-grow">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-200 sticky top-0">
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  {t('parts.orderHistoryTable.date')}
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  {t('parts.orderHistoryTable.orderNum')}
                </th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">
                  {t('parts.orderHistoryTable.customer')}
                </th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  {t('parts.orderHistoryTable.qty')}
                </th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">
                  {t('parts.orderHistoryTable.unitPrice')}
                </th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">
                  {t('parts.orderHistoryTable.currency')}
                </th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">
                  {t('parts.orderHistoryTable.status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {orderHistory.map((record, idx) => (
                <tr
                  key={idx}
                  className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-3 text-gray-700 font-medium whitespace-nowrap">
                    {formatDate(record.orderDate)}
                  </td>
                  <td className="px-4 py-3 text-gray-900 font-medium whitespace-nowrap">
                    <button
                      onClick={() => handleOrderClick(record.orderNum)}
                      className="text-blue-600 hover:text-blue-800 hover:underline font-semibold"
                    >
                      {record.orderNum}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {record.customerName}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900 font-semibold">
                    {record.orderQty.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-900 font-medium whitespace-nowrap">
                    ${record.netUnitPrice.toFixed(2)}
                    {record.discountPercent > 0 && (
                      <span className="ml-1 text-xs text-gray-500">
                        ({record.discountPercent}% DSC)
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center text-gray-700 font-medium">
                    {record.currencyCode}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        record.openLine
                          ? 'bg-green-100 text-green-800'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {record.openLine
                        ? t('parts.orderHistoryTable.open')
                        : t('parts.orderHistoryTable.closed')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="pb-2">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-tight mb-1">
                {t('parts.orderHistoryTable.date')}
              </p>
              <p className="text-sm font-medium text-gray-900">-</p>
            </div>
            <div className="pb-2 text-right">
              <p className="text-xs font-semibold text-gray-600 uppercase tracking-tight mb-1">
                {t('parts.orderHistoryTable.qty')}
              </p>
              <p className="text-sm font-medium text-gray-900">0</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Format date string for display
 * Handles ISO format, timestamps, and various date formats
 */
function formatDate(dateString: string): string {
  if (!dateString) return '-';

  try {
    let date: Date;

    // Try parsing as ISO date first
    if (dateString.includes('T') || dateString.includes('-')) {
      date = parseCalendarDate(dateString);
    } else if (/^\d+$/.test(dateString)) {
      // Handle timestamp in milliseconds
      date = new Date(parseInt(dateString, 10));
    } else {
      // Try direct Date constructor
      date = new Date(dateString);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return dateString; // Return original if parsing failed
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateString;
  }
}
